/**
 * GracePeriodWaiting — shown when a death report has been filed but the grace
 * period has not yet expired.
 *
 * Everyone who reaches this page reaches it through the executor context. It shows the countdown to
 * full access and the explanation. s229 (DEATH-EMAILS-1, board 11h):
 *   • STAGE 1 (D-229.49): the REPORTING executor also sees what opens to them now — the funeral wishes
 *     and who to call — read-only. The server decides (executor-read-gate STAGE1_SECTIONS); this page
 *     only shows the two sections, and they come back empty for anyone the gate does not open them to.
 *   • WITHDRAW (D-229.67): the reporting executor can withdraw their own report while it waits, through
 *     the existing DELETE /death-event under the single-flight lock. No "Talk to a person" (D-229.67).
 */

import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { deathEventQueryKey, useDeathEvent } from "@/lib/use-death-event";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useToast } from "@/hooks/use-toast";
import { ExpandableSectionStandalone } from "@/components/ExpandableSectionStandalone";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Clock, ShieldCheck, AlertTriangle, ArrowLeft } from "lucide-react";

interface GracePeriodWaitingProps {
  clientId: string;
}

/** Board 11h's "What is open to you now" — one read-only section, found by its slug. */
function Stage1Section({ clientId, categorySlug, sectionSlug, title, subtitle, emptyNote }: {
  clientId: string; categorySlug: string; sectionSlug: string; title: string; subtitle: string;
  /** D-229.102 C2: executor copy for an empty section — the executor cannot add anything here. */
  emptyNote: string;
}) {
  const { data: category } = useQuery<{ id: string } | null>({
    queryKey: ["/api/generic-categories/by-slug", categorySlug, clientId],
    queryFn: async () => (await apiRequest("GET", `/api/generic-categories/by-slug/${categorySlug}?clientId=${clientId}`)).json(),
  });
  const { data: sections } = useQuery<Array<{ id: string; slug: string; name: string; isActive: boolean; categoryId: string }>>({
    queryKey: ["/api/generic-categories", category?.id, "sections"],
    queryFn: async () => (await apiRequest("GET", `/api/generic-categories/${category!.id}/sections`)).json(),
    enabled: !!category?.id,
  });
  // The sections API does not filter isActive (CLAUDE.md) — filter it here.
  const section = sections?.find((s) => s.slug === sectionSlug && s.isActive);
  return (
    <div className="space-y-2" data-testid={`stage1-${sectionSlug}`}>
      <div>
        <p className="font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
      {section && (
        <ExpandableSectionStandalone
          clientId={clientId}
          sectionId={section.id}
          sectionSlug={section.slug}
          sectionName={section.name}
          categoryId={section.categoryId}
          readOnly
          emptyNote={emptyNote}
        />
      )}
    </div>
  );
}

function useCountdown(endsAtMs: number | null) {
  const [remaining, setRemaining] = useState<number>(0);

  useEffect(() => {
    if (endsAtMs == null) return;
    const update = () => setRemaining(Math.max(0, endsAtMs - Date.now()));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [endsAtMs]);

  const totalMs = remaining;
  const days = Math.floor(totalMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((totalMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((totalMs % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, totalMs };
}

export default function GracePeriodWaiting({ clientId }: GracePeriodWaitingProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const withdrawFlight = useSingleFlight();
  const withdraw = useMutation({
    mutationFn: async () => (await apiRequest("DELETE", `/api/clients/${clientId}/death-event`)).json(),
    onSuccess: () => {
      // The page redirects on its own once the event reads as gone.
      void queryClient.invalidateQueries({ queryKey: deathEventQueryKey(clientId) });
      toast({ title: "Report withdrawn", description: "Nothing opens, and the plan stays as it was." });
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't withdraw the report", description: err.message, variant: "destructive" });
    },
  });

  // re-check every minute in case it's verified externally
  const { data: deathEvent, isLoading } = useDeathEvent(clientId, { refetchInterval: 60_000 });

  const { data: contexts } = useQuery<{ myEstatePlan: any; executorFor: any[] }>({
    queryKey: ["/api/user/contexts"],
  });
  const myContext = contexts?.executorFor?.find((c: any) => c.clientId === clientId);
  const plannerName = myContext?.plannerName ?? "the plan owner";
  const plannerFirst = (myContext?.plannerName ?? "").trim().split(/\s+/)[0] || "the plan owner";
  const isReporter = deathEvent?.viewerIsReporter === true;

  const gracePeriodEndsAt = deathEvent?.gracePeriodEndsAt
    ? new Date(deathEvent.gracePeriodEndsAt)
    : null;
  const { days, hours, minutes, seconds, totalMs } = useCountdown(gracePeriodEndsAt?.getTime() ?? null);

  const gracePeriodTotal = deathEvent?.gracePeriodHours
    ? deathEvent.gracePeriodHours * 60 * 60 * 1000
    : 7 * 24 * 60 * 60 * 1000;
  const progressPct = gracePeriodTotal > 0
    ? Math.round(((gracePeriodTotal - totalMs) / gracePeriodTotal) * 100)
    : 0;


  // If no grace period event, redirect
  useEffect(() => {
    if (!isLoading && deathEvent?.status === "verified") {
      setLocation(`/executor/${clientId}/dashboard`);
    }
    if (!isLoading && (!deathEvent || deathEvent.status === "cancelled")) {
      setLocation(`/executor/${clientId}/progress`);
    }
  }, [deathEvent, isLoading, clientId, setLocation]);

  const reportedDate = deathEvent?.createdAt ? new Date(deathEvent.createdAt) : null;

  if (isLoading || !deathEvent) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-xl mx-auto">
      <div className="flex items-center gap-3">
        <Button aria-label="Back" variant="ghost" size="icon" onClick={() => setLocation(`/executor/${clientId}/progress`)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold">Grace Period Active</h1>
          <p className="text-sm text-muted-foreground">{plannerName}'s estate plan</p>
        </div>
      </div>

      {/* Countdown card */}
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            <CardTitle className="text-base text-amber-900">Full access unlocks in</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { value: days, label: "Days" },
              { value: hours, label: "Hours" },
              { value: minutes, label: "Min" },
              { value: seconds, label: "Sec" },
            ].map(({ value, label }) => (
              <div key={label} className="rounded-lg bg-card border border-amber-200 py-3">
                <div className="text-2xl font-bold text-amber-800">{String(value).padStart(2, "0")}</div>
                <div className="text-xs text-amber-600 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
          <Progress value={progressPct} className="h-2" />
          {gracePeriodEndsAt && (
            <p className="text-xs text-amber-700 text-center">
              Unlocks on{" "}
              {gracePeriodEndsAt.toLocaleDateString("en-CA", {
                weekday: "long",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          )}
        </CardContent>
      </Card>

      {/* What happens next */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">What happens during the grace period</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <div className="flex gap-3">
            <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p>
              A death was reported for <strong className="text-foreground">{plannerName}</strong>
              {reportedDate && (
                <> on {reportedDate.toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })}</>
              )}
              . The system is waiting before granting full access to verify this was not reported in error.
            </p>
          </div>
          <div className="flex gap-3">
            <Clock className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p>
              {/* D-229.102 C4: a verified death opens the whole plan to EVERY accepted executor, whatever
                  their access before (server: resolveReadScope → FULL_SCOPE; client: canAccessFullEstate). */}
              When the countdown reaches zero, the whole plan opens to every executor who has accepted,
              whatever access they had before.
            </p>
          </div>
          <div className="flex gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <p>
              {/* s229 (D-229.48 / D-229.67): the planner now has a one-click stop on every screen they sign
                  in to, so the old "can't be stopped from a screen yet" line is no longer true. Board 11h's
                  "tell us at once and we will hold everything" is replaced by Withdraw, for the reporter. */}
              If this is a mistake: {plannerFirst} has been alerted. If {plannerFirst} is alive and can sign in, they can stop the release.
            </p>
          </div>
          {isReporter && (
            <div className="pt-1">
              <Button
                variant="outline"
                data-testid="withdraw-report-button"
                disabled={withdrawFlight.inFlight}
                onClick={() => withdrawFlight.runQuiet(() => withdraw.mutateAsync())}
              >
                Withdraw the report
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isReporter && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">What is open to you now</CardTitle>
            <p className="text-sm text-muted-foreground">{plannerFirst} wanted you to have these right away</p>
          </CardHeader>
          <CardContent className="space-y-5">
            <Stage1Section clientId={clientId} categorySlug={CATEGORY_SLUGS.MY_WISHES_AND_LEGACY}
              sectionSlug={SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES}
              title={`${plannerFirst}'s funeral wishes`} subtitle={`what ${plannerFirst} asked for`}
              emptyNote={`${plannerFirst} did not record any funeral wishes.`} />
            {/* D-229.63: Important Contacts for now; switches to chapter six's "People to tell" once built. */}
            <Stage1Section clientId={clientId} categorySlug={CATEGORY_SLUGS.PERSONAL_INFORMATION}
              sectionSlug={SECTION_SLUGS.FAMILY_FRIENDS_AND_PETS_IMPORTANT_CONTACTS}
              title="Who to call" subtitle={`the people ${plannerFirst} listed as important, with numbers`}
              emptyNote={`${plannerFirst} did not list any important contacts.`} />
          </CardContent>
        </Card>
      )}

    </div>
  );
}
